/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery } from '@tanstack/react-query';
import { ClientsService } from '@services/requests/housien-services/ClientsService';

export interface ClientAttendanceNotice {
  /** Whether another broker holds an open attendance for this client. */
  hasOpenAttendance: boolean;
  /** The e-mails of the brokers holding them, deduplicated. */
  brokerEmails: string[];
  /** How many each of those brokers holds, keyed by e-mail. */
  brokerCounts: Record<string, number>;
}

const NO_NOTICE: ClientAttendanceNotice = {
  hasOpenAttendance: false, brokerEmails: [], brokerCounts: {},
};

/**
 * Whether a colleague already attends this client.
 *
 * Every broker reads every client, but a broker reads only their own
 * attendances -- so `useClientOpenAttendance` comes back empty for a
 * colleague's client and the file reads as free. This asks the server
 * the one question that listing cannot answer, and gets back no deal
 * id, stage or value: just the collision and who holds it.
 *
 * @param agencyId - The owning agency.
 * @param clientId - The client being opened; empty disables the query.
 * @returns The notice; empty while loading or on failure, so a hiccup
 *   in this read never blocks the profile from rendering.
 */
export function useClientAttendanceNotice(
  agencyId: string,
  clientId: string,
): ClientAttendanceNotice {
  const { data } = useQuery({
    queryKey: ['clients', agencyId, 'attendance-status', clientId] as const,
    queryFn: () => ClientsService.getAgencyClientAttendanceStatus(agencyId, clientId),
    enabled: Boolean(agencyId && clientId),
  });

  if (!data?.has_open_attendance) return NO_NOTICE;
  return {
    hasOpenAttendance: true,
    brokerEmails: data.brokers ?? [],
    brokerCounts: data.broker_counts ?? {},
  };
}
